import { FC, useEffect, useMemo, useState } from 'react';
import Button from '@srm/ui/button';
import { BackgroundJobKind, IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';
import { BackgroundJobsService } from '@services/BackgroundJobsService';
import { backgroundJobsStore } from '@services/backgroundJobs/BackgroundJobsStore';
import { useJobDownloads } from '@services/backgroundJobs/useBackgroundJobs';
import { JOB_ACTION_ICONS } from '@services/backgroundJobs/jobActionIcons';
import {
    BackgroundJobAction,
    backgroundJobKind,
    IBackgroundJobActionHandlers,
    JOB_ACTION_LABELS,
    jobActions,
    runJobAction,
} from '@services/backgroundJobs/backgroundJobKinds';
import { notificationTextLink } from './notificationTextLink';

export interface IJobNotificationActionsProps {
    /** Задача, об итоге которой уведомление (`targetObjectKey`); `null` — уведомление без ключа задачи. */
    jobId: string | null;
    /** Вид задачи по тегу уведомления. */
    kind: BackgroundJobKind;
    /** Текст уведомления: ссылка из него — действие, когда задачи уже нет (`textLink` вида). */
    text?: string | null;
    tabIndex?: number;
    onOpenDocument: (docId: string) => void;
    onOpenLink: (href: string) => void;
}

type JobInfo = IBackgroundJob | 'missing' | 'unknown';

/** О задаче ничего не узнать из-за сбоя: файл предлагается — сервер сам скажет, если его нет. */
const UNKNOWN_JOB_ACTIONS: BackgroundJobAction[] = [{ type: 'download', label: JOB_ACTION_LABELS.download }];

/**
 * Действия детали уведомления об итоге фоновой задачи — из реестра видов, первое основное. Кнопки ждут ответа о
 * задаче, чтобы не мигать. Задачи уже нет (уборка) — действие из ссылки в тексте, если вид её даёт, иначе ничего.
 */
const JobNotificationActions: FC<IJobNotificationActionsProps> = ({
    jobId,
    kind,
    text,
    tabIndex,
    onOpenDocument,
    onOpenLink,
}) => {
    const [info, setInfo] = useState<JobInfo | null>(jobId ? null : 'missing');
    const downloads = useJobDownloads();
    const downloading = jobId !== null && downloads.has(jobId);
    const view = backgroundJobKind(kind);
    const textLink = view.textLink;
    const textAction = useMemo<BackgroundJobAction | null>(() => {
        const href = textLink ? notificationTextLink(text, textLink.kind) : null;
        return textLink && href ? { type: 'openLink', label: textLink.label, href } : null;
    }, [text, textLink]);

    useEffect(() => {
        if (!jobId) {
            setInfo('missing');
            return undefined;
        }
        let alive = true;
        setInfo(null);
        BackgroundJobsService.get(jobId)
            .then((job) => alive && setInfo(job ?? 'missing'))
            .catch(() => alive && setInfo('unknown'));
        return () => {
            alive = false;
        };
    }, [jobId]);

    if (info === null) return null;

    const handlers: IBackgroundJobActionHandlers = {
        download: () => {
            if (!jobId) return;
            // Не скачалось — файла, возможно, уже нет: кнопка уходит, если задача так и скажет.
            void backgroundJobsStore.downloadFile(jobId, kind).then((ok) => {
                if (ok) return;
                BackgroundJobsService.get(jobId)
                    .then((job) => setInfo(job ?? 'missing'))
                    .catch(() => undefined);
            });
        },
        openDocument: (docId) => onOpenDocument(docId),
        openLink: (href) => onOpenLink(href),
    };
    const fromJob = typeof info === 'object' ? jobActions(info) : [];
    const fallback = textAction ? [textAction] : info === 'unknown' && view.resultFile ? UNKNOWN_JOB_ACTIONS : [];
    const actions = fromJob.length > 0 ? fromJob : fallback;

    return (
        <>
            {actions.map((action, index) => {
                const Icon = JOB_ACTION_ICONS[action.type];
                return (
                    <Button
                        key={`${action.type}-${index}`}
                        variant={index === 0 ? 'primary' : 'secondary'}
                        size="sm"
                        tabIndex={tabIndex}
                        loading={action.type === 'download' && downloading}
                        startAdornment={<Icon size="16" />}
                        onClick={() => runJobAction(action, jobId ?? '', handlers)}
                    >
                        {action.label}
                    </Button>
                );
            })}
        </>
    );
};

export default JobNotificationActions;
